<template>
    <div class="update-creative-carousel modal-content" :class="{ 'modal-content-bundle': isBundle }">
        <div class="relative">
            <template v-if="currentCreative">
                <!-- Header -->
                <div class="modal-header">
                    <h5 class="modal-title">Review Creative</h5>
                    <div class="modal-buttons">
                        <download-order-items download-type="creative" :query="{ filter: { id: creativeIds } }" />
                        <share-order-items share-type="creative" :query="{ filter: { id: creativeIds } }" />
                        <button type="button" class="btn-close"
                                :class="{ 'text-white opacity-100': isBundle, 'text-black': !isBundle }" @click="close">
                            <span class="fal fa-fw fa-times"></span>
                            <span class="sr-only">Close Modal</span>
                        </button>
                    </div>
                </div>

                <!-- Item Name -->
                <div class="px-3 pt-1 text-lg roboto">
                    {{ itemName }}
                    <small v-if="isBundle">in {{ parentName }}</small>
                </div>

                <!-- Carousel -->
                <div class="jgile-carousel">
                    <div class="modal-body">
                        <!-- Counter -->
                        <div class="counter">
                            {{ currentPage }} of {{ total }} Total Creative<br />
                            <run-dates :start="startDate" :end="endDate" />
                        </div>

                        <!-- Carousel Navigation -->
                        <div class="row switcher">
                            <!-- Previous Button -->
                            <div v-if="total > 1" class="col-2">
                                <button class="btn prev" @click="previous" :disabled="loading">
                                    <i class="fal fa-chevron-left fa-fw"></i>
                                    <span class="sr-only">Previous Creative</span>
                                </button>
                            </div>

                            <!-- Creative Update Form -->
                            <div :class="total > 1 ? 'col-8' : 'col-12'">
                                <div class="current-item">
                                    <div class="current-item-content">
                                        <div v-if="loading" class="text-center py-4">
                                            <i class="fa fa-spinner fa-spin"></i>
                                        </div>
                                        <div v-else class="form text-left w-100">
                                            <!-- Creative Preview -->
                                            <div class="form-group">
                                                <div class="display-creative">
                                                    <div class="display-media">
                                                        <creative-display :creative="displayCreative" />
                                                    </div>
                                                </div>
                                            </div>

                                            <hr />

                                            <!-- Request Info -->
                                            <div class="row">
                                                <div class="col">
                                                    <div class="form-group mt-2">
                                                        <label class="d-block">Request Date</label>
                                                        {{ formatDate(orderStatusDate) }}
                                                    </div>
                                                    <div class="form-group mt-2">
                                                        <label class="d-block">Request Due Date</label>
                                                        {{ formatDate(dueDate) }}
                                                    </div>
                                                </div>
                                                <div class="col">
                                                    <div class="form-group mt-2">
                                                        <label class="d-block">Reason for change</label>
                                                        {{ currentCreative.status_reason }}
                                                    </div>
                                                    <div v-if="currentCreative.status_notes" class="form-group">
                                                        <label class="d-block">Notes</label>
                                                        {{ currentCreative.status_notes }}
                                                    </div>
                                                </div>
                                            </div>

                                            <hr />

                                            <!-- Creative Requirements -->
                                            <div class="form-group">
                                                <div class="text-left">
                                                    <label>Creative Requirements</label>
                                                    <div v-if="artworkUrl" class="d-block">
                                                        <a class="btn btn-link btn-sm mb-2" :href="artworkUrl"
                                                           target="_blank"> Download Creative Document </a>
                                                    </div>
                                                    <div v-if="artworkText"
                                                         class="shadow-sm scroll-text text-left w-100">
                                                        {{ artworkText }}
                                                    </div>
                                                </div>

                                                <!-- Image Creative -->
                                                <template v-if="isImage">
                                                    <p class="mt-2"><strong>Type of Media:</strong> Image</p>
                                                    <p v-if="currentCreative.spec_width && currentCreative.spec_height">
                                                        <strong>Dimensions:</strong>
                                                        {{ currentCreative.spec_width }}px (width) x
                                                        {{ currentCreative.spec_height }}px (height)
                                                    </p>
                                                    <t-file :id="uploaderId" :file-types="['image']" :multiple="false"
                                                            :attach-to="attachTo" @input="onFileUploaded" />
                                                </template>

                                                <!-- Audio/Video Creative -->
                                                <template v-else-if="isAudioVideo">
                                                    <p class="mt-2 mb-4"><strong>Type of Media:</strong> Audio/Video</p>
                                                    <t-file :id="uploaderId" :file-types="['video', 'audio']"
                                                            :multiple="false" :attach-to="attachTo"
                                                            @input="onFileUploaded" />
                                                </template>

                                                <!-- Text Creative -->
                                                <template v-else-if="isText">
                                                    <p class="mt-2 mb-4"><strong>Type of Media:</strong> Text</p>
                                                    <div class="form-group">
                                                        <h4>Copy/Text</h4>
                                                        <textarea v-model="text" class="form-control" rows="4"
                                                                  placeholder="Enter your text here..."></textarea>
                                                    </div>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Next Button -->
                            <div v-if="total > 1" class="col-2">
                                <button class="btn next" @click="next" :disabled="loading">
                                    <i class="fal fa-chevron-right fa-fw"></i>
                                    <span class="sr-only">Next Creative</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Footer -->
                    <div class="modal-footer">
                        <ul class="list-inline">
                            <li>
                                Need Design Help?
                                <button class="btn text-blue btn-plain d-inline" @click="$emit('contact-support')">
                                    Contact Our Team of Designers
                                </button>
                            </li>
                        </ul>
                        <button class="btn btn-secondary fixed-width ml-auto" :disabled="!canSave || saving"
                                @click="save">
                            <span v-if="saving"> <i class="fa fa-spinner fa-spin"></i> Saving... </span>
                            <span v-else>Save</span>
                        </button>
                        <button v-if="isDone" id="doneButton" class="btn btn-primary fixed-width" @click="close">Done
                        </button>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import CreativeTableApi from '@js/services/CreativeTableApi';

export default {
    name: 'UpdateCreativeCarousel',

    props: {
        /**
         * Array of creative IDs to update.
         */
        creativeIds: {
            type: Array,
            required: true,
        },

        /**
         * Order ID for context.
         */
        orderId: {
            type: [Number, String],
            required: true,
        },

        /**
         * Whether this is an order-level view.
         */
        isOrder: {
            type: Boolean,
            default: false,
        },

        /**
         * Initial page to display.
         */
        initialPage: {
            type: Number,
            default: 1,
        },

        /**
         * Order status date for request date display.
         */
        orderStatusDate: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            creativeItems: [],
            currentPage: this.initialPage,
            loading: false,
            saving: false,
            text: '',
            originalText: '',
            hasReplacementMedia: false,
            uploadedMedia: null,
        };
    },

    computed: {
        /**
         * Total number of creative items.
         */
        total() {
            return this.creativeItems.length;
        },

        /**
         * Current creative being displayed.
         */
        currentCreative() {
            if (this.total === 0) return null;
            const index = this.currentPage - 1;
            return this.creativeItems[index] || null;
        },

        /**
         * Whether current creative is part of a bundle.
         */
        isBundle() {
            if (!this.currentCreative || !this.currentCreative.model) return false;
            return this.currentCreative.model.parent_id != null;
        },

        /**
         * Item name to display.
         */
        itemName() {
            if (!this.currentCreative) return '';
            if (this.currentCreative.model && this.currentCreative.model.name) {
                return this.currentCreative.model.name;
            }
            return this.currentCreative.name || '';
        },

        /**
         * Parent item name for bundles.
         */
        parentName() {
            if (!this.currentCreative || !this.currentCreative.parent_order_item) return '';
            return this.currentCreative.parent_order_item.name || '';
        },

        /**
         * Start date for run dates display.
         */
        startDate() {
            if (!this.currentCreative) return null;
            if (this.currentCreative.start_date) return this.currentCreative.start_date;
            if (this.currentCreative.model && this.currentCreative.model.start_date) {
                return this.currentCreative.model.start_date;
            }
            return null;
        },

        /**
         * End date for run dates display.
         */
        endDate() {
            if (!this.currentCreative) return null;
            if (this.currentCreative.end_date) return this.currentCreative.end_date;
            if (this.currentCreative.model && this.currentCreative.model.end_date) {
                return this.currentCreative.model.end_date;
            }
            return null;
        },

        /**
         * Due date (2 days after request).
         */
        dueDate() {
            if (!this.orderStatusDate) return null;
            const date = new Date(this.orderStatusDate);
            date.setDate(date.getDate() + 2);
            return date.toISOString();
        },

        /**
         * Artwork document URL.
         */
        artworkUrl() {
            if (!this.currentCreative || !this.currentCreative.asset) return null;
            return this.currentCreative.asset.artwork_url || null;
        },

        /**
         * Artwork text/requirements.
         */
        artworkText() {
            if (!this.currentCreative || !this.currentCreative.asset) return null;
            return this.currentCreative.asset.artwork_text || null;
        },

        /**
         * Creative type checks.
         */
        isImage() {
            return this.currentCreative && this.currentCreative.creative_type === 'image';
        },

        isAudioVideo() {
            return this.currentCreative && this.currentCreative.creative_type === 'audio_video';
        },

        isText() {
            return this.currentCreative && this.currentCreative.creative_type === 'text';
        },

        /**
         * Unique ID for the file uploader.
         */
        uploaderId() {
            if (!this.currentCreative) return 'update-creative-uploader';
            return `update-creative-uploader-${this.currentCreative.id}`;
        },

        /**
         * Attach-to configuration for file upload.
         */
        attachTo() {
            if (!this.currentCreative) return null;
            return {
                model_name: 'App\\Creative',
                model_id: this.currentCreative.id,
                collection_name: 'replace_creative',
            };
        },

        /**
         * Whether changes can be saved.
         */
        canSave() {
            if (this.isText) {
                return this.text !== this.originalText;
            }
            return this.hasReplacementMedia;
        },

        /**
         * Creative object for display - shows replacement media if uploaded.
         */
        displayCreative() {
            if (!this.currentCreative) return null;
            if (this.uploadedMedia) {
                // The media response from API uses 'full_url' for the URL
                const mediaUrl = this.uploadedMedia.full_url || this.uploadedMedia.url;
                return {
                    ...this.currentCreative,
                    media_url: mediaUrl,
                    media: {
                        ...this.uploadedMedia,
                        url: mediaUrl,
                    },
                };
            }
            return this.currentCreative;
        },

        /**
         * Whether all creative has been updated (no more change_requested status).
         */
        isDone() {
            return this.creativeItems.every(c =>
                c.status !== 'admin_change_requested' &&
                c.status !== 'team_change_requested'
            );
        },
    },

    watch: {
        creativeIds: {
            handler() {
                this.loadCreatives();
            },
            immediate: true,
        },

        currentCreative: {
            handler(newCreative) {
                if (newCreative) {
                    this.setCreativeData(newCreative);
                }
            },
        },
    },

    created() {
        Bus.$on('refreshReviewCreative', this.loadCreatives);
        Bus.$on('fileUploaded', this.onFileUploaded);
    },

    beforeDestroy() {
        Bus.$off('refreshReviewCreative', this.loadCreatives);
        Bus.$off('fileUploaded', this.onFileUploaded);
    },

    methods: {
        /**
         * Load creative items from API.
         * Only loads creative with change_requested or change_applied status.
         */
        async loadCreatives() {
            if (this.creativeIds.length === 0) return;

            this.loading = true;
            try {
                const response = await CreativeTableApi.getByIds(this.creativeIds);
                const allCreative = response.data.data || response.data;

                // Filter to only show creative that needs updates
                this.creativeItems = allCreative.filter(c =>
                    c.status === 'admin_change_requested' ||
                    c.status === 'team_change_requested' ||
                    c.status === 'admin_change_applied' ||
                    c.status === 'team_change_applied'
                );

                // Reset page if out of bounds
                if (this.currentPage > this.total && this.total > 0) {
                    this.currentPage = 1;
                }
            } catch (error) {
                Bus.$emit('toast', 'Failed to load creative items.', 'error');
            } finally {
                this.loading = false;
            }
        },

        /**
         * Set data for current creative.
         */
        setCreativeData(creative) {
            this.hasReplacementMedia = false;
            this.uploadedMedia = null;

            if (creative.creative_type === 'text') {
                this.text = creative.text || '';
                this.originalText = creative.text || '';
            } else {
                this.text = '';
                this.originalText = '';
            }
        },

        /**
         * Go to previous creative.
         */
        previous() {
            if (this.currentPage === 1) {
                this.currentPage = this.total;
            } else {
                this.currentPage--;
            }
            this.$emit('page-change', this.currentPage);
        },

        /**
         * Go to next creative.
         */
        next() {
            if (this.currentPage === this.total) {
                this.currentPage = 1;
            } else {
                this.currentPage++;
            }
            this.$emit('page-change', this.currentPage);
        },

        /**
         * Handle file upload completion.
         */
        onFileUploaded(response) {
            this.hasReplacementMedia = true;
            this.uploadedMedia = response;
        },

        /**
         * Save the updated creative.
         */
        async save() {
            this.saving = true;

            try {
                const data = {};

                if (this.isText) {
                    data.text = this.text;
                }

                const response = await CreativeTableApi.applyChange(this.currentCreative.id, data);

                // Update the current creative's status in our local array
                const updatedCreative = response.data.data || response.data;
                const currentIndex = this.currentPage - 1;
                if (this.creativeItems[currentIndex]) {
                    this.creativeItems[currentIndex] = {
                        ...this.creativeItems[currentIndex],
                        ...updatedCreative,
                    };
                }

                // Update original values
                this.originalText = this.text;
                this.hasReplacementMedia = false;
                this.uploadedMedia = null;

                this.$emit('saved');
                Bus.$emit('reloadSidebar');
                Bus.$emit('toast', 'Creative updated successfully.', 'success');
            } catch (error) {
                Bus.$emit('toast', 'Failed to save creative.', 'error');
            } finally {
                this.saving = false;
            }
        },

        /**
         * Format date for display.
         */
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
        },

        /**
         * Close the carousel modal.
         */
        close() {
            this.$emit('close');
            Bus.$emit('closeReviewCreative');
        },
    },
};
</script>

<style scoped>
.update-creative-carousel {
    width: 100%;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border-bottom: 1px solid #dee2e6;
}

.modal-title {
    margin: 0;
    font-size: 1.25rem;
}

.modal-buttons {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-close {
    background: transparent;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.25rem;
}

.modal-body {
    padding: 1rem;
}

.counter {
    text-align: center;
    margin-bottom: 1rem;
    font-size: 0.9rem;
    color: #666;
}

.switcher {
    align-items: flex-start;
}

.current-item {
    display: flex;
    justify-content: center;
}

.current-item-content {
    width: 100%;
}

.btn.prev,
.btn.next {
    padding: 10px 15px;
    background: transparent;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
}

.btn.prev:hover:not(:disabled),
.btn.next:hover:not(:disabled) {
    background-color: #f5f5f5;
}

.modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 1rem;
    border-top: 1px solid #dee2e6;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.fixed-width {
    min-width: 80px;
}

.list-inline {
    margin: 0;
    padding: 0;
    list-style: none;
    margin-right: auto;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.form-control {
    display: block;
    width: 100%;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
    color: #495057;
    background-color: #fff;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
}

.form-control:focus {
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.scroll-text {
    max-height: 150px;
    overflow-y: auto;
    padding: 10px;
    background: #f8f9fa;
    border-radius: 4px;
}

.display-creative {
    text-align: center;
}

.display-media {
    display: flex;
    justify-content: center;
}

.text-blue {
    color: #007bff;
}

.btn-plain {
    background: transparent;
    border: none;
    padding: 0;
    text-decoration: underline;
    cursor: pointer;
}

.modal-content-bundle .modal-header {
    background-color: #343a40;
    color: white;
}

hr {
    margin: 1rem 0;
    border: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
}
</style>
